import React, { useState, useEffect } from 'react';
import { Link, Redirect } from 'react-router-dom';
import userStore from '../../../Stores/User';
import { observer } from 'mobx-react';
import Play from '../../DiscoverNew/Icons/Play.svg';
import Pause from '../../DiscoverNew/Icons/Pause.svg';
import { loadStripe } from '@stripe/stripe-js';
import mixpanel from 'mixpanel-browser';
import loadingIcon from '../../../Assets/loading.gif';
import { createSubscriptionCheckoutSessionWithTrial, validateCoupon, cancelSetupIntent } from '../../../audiblogsApi';
import queryString from 'query-string';
import CheckoutForm from '../../CheckoutForm';

const STRIPE_API_KEY = process.env.REACT_APP_STRIPE_API_KEY;
const stripePromise = loadStripe(STRIPE_API_KEY);

const InitialCheckoutForm = observer(({ onCouponSubmit, couponError, couponSuccess, onAddPaymentDetails, couponCode, setCouponCode, isLoading }) => {
  return (
    <div>
      {/* Coupon Input */}
      <div style={{ 
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '15px'
      }}>
        <input
          type="text"
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value)}
          placeholder="Coupon code"
          style={{
            flex: 1,
            padding: '8px 12px',
            backgroundColor: 'rgba(24,15,34,1)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
        <button
          onClick={() => onCouponSubmit(couponCode)}
          disabled={isLoading}
          style={{
            padding: '8px 12px',
            backgroundColor: 'transparent',
            color: '#fff',
            border: '1px solid #fff',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            opacity: isLoading ? 0.7 : 1
          }}
        >
          {isLoading ? 'Applying...' : 'Apply'}
        </button>
      </div>

      {/* Coupon Messages */}
      {couponError && (
        <div style={{ 
          color: '#df1b41', 
          fontSize: '14px',
          marginBottom: '15px',
          display: 'flex',
          alignItems: 'center',
          gap: '5px'
        }}>
          <span style={{ fontWeight: 'bold' }}>✕</span> {couponError}
        </div>
      )}
      
      {couponSuccess && (
        <div style={{ 
          color: '#4ade80', 
          fontSize: '14px',
          marginBottom: '15px',
          display: 'flex',
          alignItems: 'center',
          gap: '5px'
        }}>
          <span style={{ fontWeight: 'bold' }}>✓</span> {couponSuccess}
        </div>
      )}

      {/* Add Payment Details Button */}
      <div 
        onClick={onAddPaymentDetails}
        role="button"
        style={{
          width: '100%',
          padding: '12px',
          backgroundColor: '#3b82f6 !important',
          background: '#3b82f6 !important',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '16px',
          textAlign: 'center',
          userSelect: 'none'
        }}
      >
        Add Payment Details
      </div>

      {/* Terms - Only show in InitialCheckoutForm */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: '20px',
        fontSize: '12px',
        color: '#666'
      }}>
        By continuing, you agree to our{' '}
        <a 
          href="/terms" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ color: '#fff', textDecoration: 'underline' }}
        >
          Terms of Service
        </a>
      </div>
    </div>
  );
});

const ChooseVoice = observer((props) => {
  const [quality, setQuality] = useState('Unreal');
  const [subscriptionType, setSubscriptionType] = useState(() => {
    // Get forced variant from URL params first, then fallback to mixpanel
    const forcedVariant = queryString.parse(props.location.search).variant?.toLowerCase();
    const landingPage = mixpanel.get_property('utm_landingpage');
    const isVariantB = forcedVariant === 'b' || landingPage === 'landing_b';
    return isVariantB ? 'pro-monthly' : 'basic-monthly';
  });
  const [sessionId, setSessionId] = useState(null);
  const params = queryString.parse(props.location.search);
  const app = params.app || '';
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showPaymentElement, setShowPaymentElement] = useState(false);
  const [paymentKey, setPaymentKey] = useState(0);
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [setupIntentId, setSetupIntentId] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [landingVariant, setLandingVariant] = useState('A');

  // Helper function to estimate audio duration
  const estimateAudioDuration = (charCount) => {
    // More accurate estimate: 10k chars ≈ 13 minutes of audio
    // This accounts for natural pauses, punctuation, and speech rate
    const minutes = Math.round((charCount / 10000) * 10.5);
    return minutes;
  };

  useEffect(() => {
    if (!userStore.user) {
      setShouldRedirect(true);
      return;
    }
    if (userStore.user && userStore.user.vip_id) {
      setShouldRedirect(true);
    }

    // Check for forced variant in URL params first, then fallback to mixpanel
    const forcedVariant = params.variant?.toLowerCase();
    if (forcedVariant === 'a' || forcedVariant === 'b') {
      setLandingVariant(forcedVariant.toUpperCase());
      if (forcedVariant === 'b') {
        setSubscriptionType('pro-monthly');
      }
    } else {
      // Get landing page variant from mixpanel
      const landingPage = mixpanel.get_property('utm_landingpage');
      console.log('landingPage', landingPage);
      if (landingPage === 'landing_b') {
        setLandingVariant('B');
        setSubscriptionType('pro-monthly');
      }
    }

    // Simulate initial loading
    setTimeout(() => {
      setIsInitialLoading(false);
    }, 1500);
  }, [userStore.user, params.variant]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (showPaymentElement) {
      setShowPaymentElement(true);
    }
  }, [showPaymentElement]);

  const handleCouponSubmit = async (code) => {
    if (!code.trim()) return;
    setIsLoading(true);
    try {
      const result = await validateCoupon(userStore.idToken, code.trim());
      if (result.error) {
        setCouponError(result.error);
      } else if (result.valid && result.coupon_details) {
        setCouponSuccess(formatCouponMessage(result.coupon_details));
        setCouponCode(code);
      } else {
        setCouponError('Invalid coupon code');
      }
    } catch (error) {
      setCouponError('Failed to validate coupon');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCouponMessage = (couponDetails) => {
    let message = '';
    if (couponDetails.percent_off) {
      message = `${couponDetails.percent_off}% off`;
    } else if (couponDetails.amount_off) {
      const amount = (couponDetails.amount_off / 100).toFixed(2);
      const currency = couponDetails.currency.toUpperCase();
      message = `${currency} ${amount} off`;
    }

    if (couponDetails.duration === 'repeating' && couponDetails.duration_in_months) {
      message += ` for ${couponDetails.duration_in_months} months`;
    } else if (couponDetails.duration === 'once') {
      message += ` (one-time)`;
    } else if (couponDetails.duration === 'forever') {
      message += ` forever`;
    }

    if (couponDetails.restrictions?.minimum_amount) {
      const minAmount = (couponDetails.restrictions.minimum_amount / 100).toFixed(2);
      const currency = couponDetails.restrictions.minimum_amount_currency.toUpperCase();
      message += ` (min. ${currency} ${minAmount})`;
    }

    return message || 'Coupon applied successfully!';
  };

  const handlePaymentDetails = () => {
    setPaymentKey(prev => prev + 1);
    setShowPaymentElement(true);
  };

  const handleChangePlan = async () => {
    if (setupIntentId) {
      setIsLoading(true);
      try {
        await cancelSetupIntent(userStore.idToken, setupIntentId);
        setSetupIntentId(null);
        setShowPaymentElement(false);
        localStorage.removeItem('lastSetupIntentId');
        setPaymentKey(prev => prev + 1);
      } catch (error) {
        console.error('Error canceling setup intent:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSubscriptionTypeChange = (newType) => {
    if (!showPaymentElement) {
      setSubscriptionType(newType);
    }
  };

  if (shouldRedirect) {
    const searchParams = new URLSearchParams();
    if (app) searchParams.set('app', app);
    if (params.variant) searchParams.set('variant', params.variant);
    const queryString = searchParams.toString();
    return <Redirect to={userStore.user ? `quickstart?${queryString}` : `/signin?redirected_from=signup&${queryString}`} />;
  }

  if (isInitialLoading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#180f22',
        zIndex: 1000
      }}>
        <div style={{
          position: 'relative',
          width: '120px',
          height: '120px',
          marginBottom: '20px'
        }}>
          <img 
            src="/logo192.png" 
            alt="Audioread" 
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              animation: 'pulse 2s infinite'
            }}
          />
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '140px',
            height: '140px',
            border: '3px solid rgba(255,255,255,0.1)',
            borderTopColor: 'rgb(255 143 65)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
        <style>
          {`
            @keyframes pulse {
              0% { transform: scale(1); opacity: 1; }
              50% { transform: scale(1.1); opacity: 0.8; }
              100% { transform: scale(1); opacity: 1; }
            }
            @keyframes spin {
              0% { transform: translate(-50%, -50%) rotate(0deg); }
              100% { transform: translate(-50%, -50%) rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  const firstName = userStore.user?.displayName?.split(' ')[0];
  const welcomeMessage = firstName ? `👋 Hi ${firstName},\ntry a premium plan free for 7 days` : 'Start your free trial';

  return (
    <div className="center-static" style={{ 
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100%',
      padding: '20px',
      boxSizing: 'border-box',
      overflow: 'auto'
    }}>
      <h1 className="headline" style={{ marginBottom: '0px', whiteSpace: 'pre-line' }}>
        {welcomeMessage}
      </h1>

      {/* Social Proof */}
      <div style={{
        textAlign: 'center',
        margin: '20px 0',
        padding: '15px',
        backgroundColor: 'rgba(255,255,255,0.05)',
        borderRadius: '8px',
        fontSize: '14px'
      }}>
        <div style={{ marginBottom: '10px' }}>⭐️ Join 100,000+ happy listeners</div>
        <div style={{ opacity: 0.8 }}>"This app has completely changed how I consume content" - Sarah K.</div>
      </div>

      {/* Urgency */}
      <div style={{
        textAlign: 'center',
        margin: '20px 0',
        padding: '15px',
        backgroundColor: 'rgba(255,143,65,0.1)',
        borderRadius: '8px',
        fontSize: '14px',
        border: '1px solid rgba(255,143,65,0.3)'
      }}>
        <div style={{ color: 'rgb(255 143 65)', fontWeight: 'bold', fontSize: '16px' }}>🔥 EXCLUSIVE 1-TIME OFFER</div>
        <div style={{ marginTop: '8px', opacity: 0.9 }}>
          <div style={{ marginBottom: '4px' }}>🎁 7-day free trial + 50% off your first payment</div>
          <div style={{ fontSize: '13px', opacity: 0.8 }}>Use code <span style={{ fontWeight: 'bold', color: 'rgb(255 143 65)' }}>HALFOFF</span> - Works on both monthly & yearly plans!</div>
          <div style={{ fontSize: '12px', marginTop: '6px', fontStyle: 'italic', opacity: 0.7 }}>This offer is only available when starting your trial now. If you sign up later, you'll miss out on the 50% discount.</div>
        </div>
      </div>

      <h2 className="subheadline" style={{ whiteSpace: 'pre-line' }}>Choose your plan</h2>
      
      {/* Subscription Type Selection */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        gap: '10px', 
        margin: '20px 0',
        opacity: showPaymentElement ? 0.5 : 1,
        pointerEvents: showPaymentElement ? 'none' : 'auto',
        flexWrap: 'wrap'
      }}>
        <div 
          onClick={() => handleSubscriptionTypeChange(subscriptionType.includes('pro') ? 'pro-monthly' : 'basic-monthly')}
          style={{
            padding: '8px 20px',
            border: '2px solid hsla(0,0%,100%,.1)',
            borderRadius: '20px',
            cursor: showPaymentElement ? 'not-allowed' : 'pointer',
            backgroundColor: subscriptionType.includes('monthly') ? 'rgba(255,255,255,0.1)' : 'transparent',
          }}
        >
          Monthly
        </div>

        <div
          onClick={() => handleSubscriptionTypeChange(subscriptionType.includes('pro') ? 'pro-yearly' : 'basic-yearly')} 
          style={{
            padding: '8px 20px',
            border: '2px solid hsla(0,0%,100%,.1)',
            borderRadius: '20px',
            cursor: showPaymentElement ? 'not-allowed' : 'pointer',
            backgroundColor: subscriptionType.includes('yearly') ? 'rgba(255,255,255,0.1)' : 'transparent',
          }}
        >
          Yearly <span style={{ color: '#4CAF50' }}>(Save 17%)</span>
        </div>
      </div>

      {/* Plan Selection Cards */}
      <div 
        style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'center', 
          alignItems: 'center',
          gap: '20px', 
          margin: '20px 0',
          width: '100%',
          maxWidth: '100%',
          marginLeft: 'auto',
          marginRight: 'auto',
          opacity: showPaymentElement ? 0.5 : 1,
          pointerEvents: showPaymentElement ? 'none' : 'auto',
          flexWrap: 'wrap',
          boxSizing: 'border-box',
          padding: '0 20px'
        }}
      >
        {landingVariant === 'B' ? (
          <>
            {/* Free Plan Card - Only for variant B */}
            <div
              onClick={() => handleSubscriptionTypeChange('free')}
              style={{
                padding: '20px',
                border: '2px solid hsla(0,0%,100%,.1)',
                borderRadius: '8px',
                width: isMobile ? '100%' : 'calc(50% - 10px)',
                maxWidth: '300px',
                cursor: showPaymentElement ? 'not-allowed' : 'pointer',
                backgroundColor: subscriptionType === 'free' ? 'rgba(255,255,255,0.1)' : 'transparent',
                boxSizing: 'border-box'
              }}
            >
              <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>Free</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'rgb(255 143 65)' }}>$0</div>
              <div style={{ opacity: 0.8 }}>forever</div>
              <ul style={{ textAlign: 'left', marginTop: '15px', paddingLeft: '20px' }}>
                <li>🎧 Listen on your podcast player</li>
                <li>📚 Add urls, pdfs, emails, feeds+</li>
                <li>🗣️ Life-like voices</li>
                <li>📊 3 articles per month</li>
                <li>📱 Up to 3 feed subscriptions</li>
                <li>📝 Up to 10k chars (~{estimateAudioDuration(10000)} min) per article</li>
                <li style={{ opacity: 0.7 }}>📺 Includes ads</li>
              </ul>
            </div>

            {/* Pro Plan Card - Modified for variant B */}
            <div
              onClick={() => handleSubscriptionTypeChange(subscriptionType.includes('yearly') ? 'pro-yearly' : 'pro-monthly')}
              style={{
                padding: '20px',
                border: '2px solid hsla(0,0%,100%,.1)',
                borderRadius: '8px',
                width: isMobile ? '100%' : 'calc(50% - 10px)',
                maxWidth: '300px',
                cursor: showPaymentElement ? 'not-allowed' : 'pointer',
                backgroundColor: subscriptionType.includes('pro') ? 'rgba(255,255,255,0.1)' : 'transparent',
                boxSizing: 'border-box',
                position: 'relative'
              }}
            >
              <div style={{ 
                position: 'absolute',
                top: '-12px',
                right: '20px',
                backgroundColor: 'rgb(255 143 65)',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                MOST POPULAR
              </div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>Unlimited Pro</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'rgb(255 143 65)' }}>
                {subscriptionType.includes('yearly') ? '$99.99' : '$9.99'}
              </div>
              <div style={{ opacity: 0.8 }}>per {subscriptionType.includes('yearly') ? 'year' : 'month'}</div>
              <ul style={{ textAlign: 'left', marginTop: '15px', paddingLeft: '20px' }}>
                <li>✨ Everything in Free, but better</li>
                <li>♾️ Unlimited articles & feeds</li>
                <li>🎙️ Premium voices</li>
                <li>🌍 Multi-language support</li>
                <li>🤖 AI Summaries</li>
                <li>🔗 Add Feeds by URL</li>
                <li>💾 Download articles as MP3</li>
                <li>📝 Up to 100k chars (~{estimateAudioDuration(100000)} min) per article</li>
                <li>🚫 Ad-free experience</li>
              </ul>
            </div>
          </>
        ) : (
          <>
            {/* Basic Plan Card - Only for variant A */}
            <div
              onClick={() => handleSubscriptionTypeChange(subscriptionType.includes('yearly') ? 'basic-yearly' : 'basic-monthly')}
              style={{
                padding: '20px',
                border: '2px solid hsla(0,0%,100%,.1)',
                borderRadius: '8px',
                width: isMobile ? '100%' : 'calc(50% - 10px)',
                maxWidth: '300px',
                cursor: showPaymentElement ? 'not-allowed' : 'pointer',
                backgroundColor: subscriptionType.includes('basic') ? 'rgba(255,255,255,0.1)' : 'transparent',
                boxSizing: 'border-box'
              }}
            >
              <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>Unlimited Basic</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'rgb(255 143 65)' }}>
                {subscriptionType.includes('yearly') ? '$49.99' : '$4.99'}
              </div>
              <div style={{ opacity: 0.8 }}>per {subscriptionType.includes('yearly') ? 'year' : 'month'}</div>
              <ul style={{ textAlign: 'left', marginTop: '15px', paddingLeft: '20px' }}>
                <li>✨ Remove all ads</li>
                <li>🎧 Listen on your podcast player</li>
                <li>📚 Add urls, pdfs, emails, feeds+</li>
                <li>🗣️ Life-like voices</li>
                <li>♾️ Unlimited articles</li>
                <li>📱 Unlimited Feeds</li>
                <li style={{ color: '#4CAF50', fontWeight: 'bold' }}>📝 Up to 100k chars (~{estimateAudioDuration(100000)} min) per article</li>
              </ul>
            </div>

            {/* Pro Plan Card - Only for variant A */}
            <div
              onClick={() => handleSubscriptionTypeChange(subscriptionType.includes('yearly') ? 'pro-yearly' : 'pro-monthly')}
              style={{
                padding: '20px',
                border: '2px solid hsla(0,0%,100%,.1)',
                borderRadius: '8px',
                width: isMobile ? '100%' : 'calc(50% - 10px)',
                maxWidth: '300px',
                cursor: showPaymentElement ? 'not-allowed' : 'pointer',
                backgroundColor: subscriptionType.includes('pro') ? 'rgba(255,255,255,0.1)' : 'transparent',
                boxSizing: 'border-box',
                position: 'relative'
              }}
            >
              <div style={{ 
                position: 'absolute',
                top: '-12px',
                right: '20px',
                backgroundColor: 'rgb(255 143 65)',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                MOST POPULAR
              </div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>Unlimited Pro</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'rgb(255 143 65)' }}>
                {subscriptionType.includes('yearly') ? '$99.99' : '$9.99'}
              </div>
              <div style={{ opacity: 0.8 }}>per {subscriptionType.includes('yearly') ? 'year' : 'month'}</div>
              <ul style={{ textAlign: 'left', marginTop: '15px', paddingLeft: '20px' }}>
                <li>✨ Everything in Basic</li>
                <li>🎙️ Premium voices</li>
                <li>🌍 Multi-language support</li>
                <li>🤖 AI Summaries</li>
                <li>🔗 Add Feeds by URL</li>
                <li>💾 Download articles as MP3</li>
                <li style={{ color: '#4CAF50', fontWeight: 'bold' }}>📝 Up to 100k chars (~{estimateAudioDuration(100000)} min) per article</li>
              </ul>
            </div>
          </>
        )}
      </div>

      {/* Change Plan Button */}
      {showPaymentElement && (
        <div style={{ textAlign: 'center', margin: '20px 0' }}>
          <button 
            onClick={handleChangePlan}
            disabled={isLoading}
            style={{
              padding: '8px 20px',
              backgroundColor: 'transparent',
              border: '1px solid #fff',
              borderRadius: '4px',
              color: '#fff',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              width: 'fit-content',
              fontSize: '14px',
              opacity: isLoading ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              margin: '0 auto'
            }}
          >
            {isLoading ? (
              <>
                <img src={loadingIcon} alt="Loading..." style={{ width: '16px', height: '16px' }} />
                Changing Plan...
              </>
            ) : (
              'Change Plan'
            )}
          </button>
        </div>
      )}

      {/* Coupon and Payment Section */}
      <div style={{ 
        maxWidth: '100%', 
        margin: '20px auto',
        padding: '20px',
        backgroundColor: 'rgba(24,15,34,0.5)',
        borderRadius: '8px',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {subscriptionType === 'free' ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ maxWidth: '300px', margin: '0 auto' }}>
              <button
                onClick={() => {
                  mixpanel.track('Quickstart: Subscription setup done', {
                    completed: true,
                    plan: 'free'
                  });
                  mixpanel.people.set_once({
                    subscription_plan: 'free',
                    registered: true
                  });
                  props.history.push('/add');
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  textAlign: 'center'
                }}
              >
                Continue to App
              </button>
            </div>
            <div style={{ 
              textAlign: 'center', 
              marginTop: '20px',
              fontSize: '12px',
              color: '#666'
            }}>
              By continuing, you agree to our{' '}
              <a 
                href="/terms" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: '#fff', textDecoration: 'underline' }}
              >
                Terms of Service
              </a>
            </div>
          </div>
        ) : (
          <CheckoutForm 
            planType={subscriptionType} 
            key={paymentKey}
            couponCode={couponCode}
            onSetupIntentCreated={(setupIntentId) => {
              setSetupIntentId(setupIntentId);
              const plan = subscriptionType.includes('pro') ? 'pro' : 'basic';
              
              // Track subscription setup
              mixpanel.track('Quickstart: Subscription setup done', {
                completed: true,
                plan: plan,
                billing: subscriptionType.includes('yearly') ? 'yearly' : 'monthly'
              });
              
              // Track paid account signup
              mixpanel.track('Paid Account Signup', {
                subscription_type: subscriptionType,
                plan: plan,
                trial_days: 7,
                coupon_used: !!couponCode
              });

              mixpanel.people.set_once({
                subscription_plan: plan,
                registered: true
              });
            }}
            onShowPaymentElement={setShowPaymentElement}
          />
        )}
      </div>

      {/* Money Back Guarantee */}
      <div style={{
        textAlign: 'center',
        margin: '20px auto',
        maxWidth: '600px',
        padding: '15px',
        backgroundColor: 'rgba(76,175,80,0.1)',
        borderRadius: '8px',
        fontSize: '14px',
        border: '1px solid rgba(76,175,80,0.3)'
      }}>
        <div style={{ color: '#4CAF50', fontWeight: 'bold', marginBottom: '5px' }}>🔒 100% Money-Back Guarantee</div>
        <div style={{ opacity: 0.8 }}>If you're not satisfied within 30 days, we'll refund your payment. No questions asked.</div>
      </div>

      {/* FAQ Section */}
      <div style={{
        maxWidth: '600px',
        margin: '20px auto',
        padding: '20px',
        backgroundColor: 'rgba(24,15,34,0.5)',
        borderRadius: '8px'
      }}>
        <h3 style={{ marginBottom: '15px', fontSize: '18px' }}>Frequently Asked Questions</h3>
        
        <div style={{ marginBottom: '15px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>What are the article length limits?</div>
          <div style={{ opacity: 0.8, fontSize: '14px' }}>
            Free users can convert articles up to 10,000 characters (~{estimateAudioDuration(10000)} minutes of audio). 
            Paid plans (Basic and Pro) support articles up to 100,000 characters (~{estimateAudioDuration(100000)} minutes of audio).
          </div>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>What happens after the 7-day trial?</div>
          <div style={{ opacity: 0.8, fontSize: '14px' }}>Your subscription will automatically continue at the regular price. You can cancel anytime before the trial ends.</div>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Can I cancel anytime?</div>
          <div style={{ opacity: 0.8, fontSize: '14px' }}>Yes, you can cancel your subscription at any time. Your plan will stay active until the end of the current billing period.</div>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>What payment methods do you accept?</div>
          <div style={{ opacity: 0.8, fontSize: '14px' }}>We accept all major credit cards, including Visa, Mastercard, American Express, Discover, and Apple/Google Pay.</div>
        </div>

        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Is my payment information secure?</div>
          <div style={{ opacity: 0.8, fontSize: '14px' }}>Yes, we use Stripe for secure payment processing. We never store your credit card information.</div>
        </div>
      </div>
            {/* Stick with Free Button - Moved below payment section */}
            <div
        style={{ 
          maxWidth: 300, 
          margin: '20px auto', 
          textAlign: 'center',
          opacity: 0.7,
          fontSize: '14px'
        }}
      >
        <span 
          style={{ 
            cursor: 'pointer',
            textDecoration: 'underline',
            color: '#fff'
          }}
          onClick={() => {
            mixpanel.track('Quickstart: Subscription setup done', {
              completed: true,
              plan: 'free'
            });
            mixpanel.people.set_once({
              subscription_plan: 'free',
              registered: true
            });
            props.history.push('/add');
          }}
        >
          Continue with limited free version
        </span>
      </div>
    </div>
  );
});

export default ChooseVoice;
